import React, { useState } from 'react';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  TicketIcon,
  TrashIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MemberAvatar from './MemberAvatar';
import AttributeLabel from './AttributeLabel';

// Shared presentation for personal bookings and guest-card bookings.
interface EventCardData {
  id: number;
  title: string;
  start: string;
  start_time: string;
  end_time?: string;
  beschreibung?: string;
  location?: string;
  creator_name: string;
  creator_image?: string;
  creator: string;
  cotrainer?: string;
  cotrainer_name?: string;
  cotrainer_image?: string;
  participantCount: number;
  max_teilnehmer?: number;
  farbe?: string;
  cardName?: string;
  booking_date?: string;
  cardId?: number;
  kalender_id?: number;
}

interface EventCardProps {
  event: EventCardData;
  isNext?: boolean;
  showCancelButton?: boolean;
  showParticipantsButton?: boolean;
  participantCount?: number;
  loadingParticipants?: boolean;
  onCancel?: () => void;
  onToggleParticipants?: () => void;
  BASE_URL?: string;
  isGuestCardStyle?: boolean;
  cancellationStatus?: {
    allowed: boolean;
    reason?: string;
  };
}

const getUserImageUrl = (userId: string, BASE_URL: string) => {
  const token = localStorage.getItem('jwt_token');
  return `${BASE_URL}/api/members/user-image-url/${userId}?size=S&token=${token}`;
};

const withAlpha = (color: string, alpha: string) => (
  /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : 'rgba(100, 116, 139, 0.1)'
);

const TrainerAvatar: React.FC<{ userId: string; name: string; BASE_URL: string }> = ({
  userId,
  name,
  BASE_URL,
}) => {
  const [imageError, setImageError] = useState(false);
  const safeName = name || 'Unbekannt';
  // Vollständigen Namen in Vor-/Nachname aufteilen für die Initialen-Fallback-Darstellung.
  const [vorname, ...rest] = safeName.split(' ');
  const nachname = rest.join(' ');
  const imageUrl = userId && BASE_URL ? getUserImageUrl(userId, BASE_URL) : undefined;

  // Keine Rollen-Daten für Trainer vorhanden → ohne Rolle (kein Badge), nicht raten.
  return (
    <MemberAvatar
      member={{ vorname, name: nachname }}
      photoUrl={imageUrl}
      hasImageError={imageError}
      onImageError={() => setImageError(true)}
      size="sm"
      className="ring-2 ring-white dark:ring-gray-800"
    />
  );
};

const EventCard: React.FC<EventCardProps> = ({
  event,
  isNext = false,
  showCancelButton = false,
  showParticipantsButton = false,
  participantCount,
  loadingParticipants = false,
  onCancel,
  onToggleParticipants,
  BASE_URL = '',
  isGuestCardStyle = false,
  cancellationStatus,
}) => {
  const startDate = new Date(`${event.start}T${event.start_time || '00:00'}`);
  const endDate = new Date(`${event.start}T${event.end_time || event.start_time || '00:00'}`);
  const isFutureEvent = endDate >= new Date();
  const accentColor = event.farbe || (isNext ? '#65a30d' : '#6b7280');
  const visibleParticipantCount = participantCount ?? event.participantCount ?? 0;
  const statusLabel = isFutureEvent ? (isNext ? 'Als Nächstes' : 'Kommend') : 'Vergangen';
  const hasActions = showParticipantsButton || (showCancelButton && (isFutureEvent || cancellationStatus?.allowed));

  const day = startDate.toLocaleDateString('de-DE', { day: '2-digit' });
  const month = startDate.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
  const year = startDate.toLocaleDateString('de-DE', { year: 'numeric' });
  const weekday = startDate.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  const bookingDate = event.booking_date ? new Date(event.booking_date) : null;

  return (
    <article className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-[#1c2432] dark:hover:border-gray-600">
      <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: accentColor }} />

      <div className="flex flex-col sm:flex-row sm:items-stretch">
        <div className="flex min-w-0 flex-1 gap-3 p-3.5 pl-5 sm:gap-4 sm:p-4 sm:pl-6">
          <div
            className="flex h-[64px] w-14 shrink-0 flex-col items-center justify-center rounded-xl border text-center sm:h-[72px] sm:w-[68px]"
            style={{ backgroundColor: withAlpha(accentColor, '12'), borderColor: withAlpha(accentColor, '35') }}
            aria-label={startDate.toLocaleDateString('de-DE')}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{weekday}</span>
            <span className="text-2xl font-bold leading-none text-gray-900 dark:text-white sm:text-[26px]">{day}</span>
            <span className="mt-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: accentColor }}>{month} {year}</span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${isFutureEvent ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
                {isFutureEvent ? <CalendarDaysIcon className="h-3.5 w-3.5" /> : <CheckCircleIcon className="h-3.5 w-3.5" />}
                {statusLabel}
              </span>
              {isGuestCardStyle && event.cardName && (
                <span className="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-bold text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
                  <TicketIcon className="h-3.5 w-3.5" /> {event.cardName}
                </span>
              )}
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h3 className="text-base font-bold leading-snug text-gray-900 dark:text-white sm:text-lg">{event.title || 'Unbenanntes Event'}</h3>
              <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
              <div className="flex min-w-0 items-center gap-2">
                <TrainerAvatar userId={event.creator} name={event.creator_name || 'Unbekannt'} BASE_URL={BASE_URL} />
                <span className="truncate text-xs font-bold text-gray-600 dark:text-gray-300 sm:text-sm">{event.creator_name || 'Unbekannter Trainer'}</span>
              </div>
              {event.cotrainer_name?.trim() && (
                <div className="flex min-w-0 items-center gap-2 text-gray-400">
                  <span>+</span>
                  <TrainerAvatar userId={event.cotrainer || ''} name={event.cotrainer_name} BASE_URL={BASE_URL} />
                  <span className="truncate text-xs font-bold text-gray-600 dark:text-gray-300 sm:text-sm">{event.cotrainer_name}</span>
                </div>
              )}
            </div>

            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-gray-600 dark:text-gray-300 sm:text-sm">
              <AttributeLabel iconKey="time">
                {event.start_time || '00:00'}{event.end_time ? ` – ${event.end_time}` : ''} Uhr
              </AttributeLabel>
              {event.location && (
                <AttributeLabel iconKey="location" className="min-w-0" textClassName="truncate">
                  {event.location}
                </AttributeLabel>
              )}
              <AttributeLabel iconKey="participants">
                {visibleParticipantCount}{event.max_teilnehmer ? ` / ${event.max_teilnehmer}` : ''} Teilnehmer
              </AttributeLabel>
            </div>

            {bookingDate && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Gebucht am {bookingDate.toLocaleDateString('de-DE')} um {bookingDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</p>
            )}

            {event.beschreibung && (
              <p className="mt-2 line-clamp-2 rounded-xl bg-gray-50 px-3 py-2 text-sm leading-relaxed text-gray-600 dark:bg-gray-800/70 dark:text-gray-300">{event.beschreibung}</p>
            )}
          </div>
        </div>

        {hasActions && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800/40 sm:border-l sm:border-t-0 sm:bg-transparent sm:px-5 sm:py-4 sm:dark:bg-transparent">
            {showParticipantsButton && (
              <button
                onClick={onToggleParticipants}
                disabled={loadingParticipants}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 text-sm font-bold text-gray-700 transition hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                aria-label={`${visibleParticipantCount} Teilnehmer anzeigen`}
              >
                {loadingParticipants ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" /> : <UserGroupIcon className="h-4 w-4" />}
                Teilnehmer ({visibleParticipantCount})
              </button>
            )}

            {showCancelButton && (isFutureEvent || cancellationStatus?.allowed) && (
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={cancellationStatus?.allowed !== false ? onCancel : undefined}
                  disabled={cancellationStatus?.allowed === false}
                  className={`inline-flex h-9 items-center justify-center gap-2 rounded-xl px-3.5 text-sm font-bold transition ${cancellationStatus?.allowed === false ? 'cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500' : 'bg-error-50 text-error-700 hover:bg-error-100 dark:bg-error-900/20 dark:text-error-300 dark:hover:bg-error-900/30'}`}
                  aria-label={cancellationStatus?.allowed === false ? 'Stornierung nicht möglich' : 'Buchung stornieren'}
                >
                  {isGuestCardStyle ? <XMarkIcon className="h-4 w-4" /> : <TrashIcon className="h-4 w-4" />}
                  Stornieren
                </button>
                {cancellationStatus?.allowed === false && cancellationStatus.reason && <p className="max-w-xs text-right text-xs text-gray-500 dark:text-gray-400">{cancellationStatus.reason}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default EventCard;
